import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import Footer from '../components/Footer';

const AuthPage = ({ setUser }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [registrationEmail, setRegistrationEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [guestDisplayName, setGuestDisplayName] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        // Login flow (unchanged)
        const response = await axios.post(`${API}/auth/login`, {
          username: formData.username,
          password: formData.password
        });
        
        localStorage.setItem('token', response.data.access_token);
        setUser(response.data.user);
        toast.success('Login successful!');
      } else {
        // Registration flow - send OTP
        const response = await axios.post(`${API}/auth/register`, formData);
        setRegistrationEmail(formData.email);
        setShowOtpInput(true);
        
        // Check if dev mode and OTP is in response
        if (response.data.dev_mode && response.data.otp) {
          toast.success(`Registration initiated! Your OTP is: ${response.data.otp}`, { duration: 10000 });
          setOtp(response.data.otp); // Auto-fill the OTP
        } else {
          toast.success(
            <div>
              <div className="font-semibold mb-1">OTP Sent Successfully! 📧</div>
              <div className="text-sm">
                ✓ Check your inbox<br/>
                ✓ Check spam/junk folder<br/>
                ✓ Email may take 2-3 minutes<br/>
                ✓ OTP expires in 10 minutes
              </div>
            </div>,
            { duration: 8000 }
          );
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'An error occurred';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerification = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/verify-otp`, {
        email: registrationEmail,
        otp: otp
      });
      
      localStorage.setItem('token', response.data.access_token);
      setUser(response.data.user);
      toast.success('Registration successful! Welcome to ChatHub!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid OTP';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/resend-otp`, { email: registrationEmail });
      toast.success('New OTP sent to your email!');
    } catch (error) {
      toast.error('Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleGuestLogin = async () => {
    const trimmedGuestName = guestDisplayName.trim();
    if (!trimmedGuestName) {
      toast.error('Please enter a guest display name');
      return;
    }
    if (trimmedGuestName.length < 3 || trimmedGuestName.length > 24) {
      toast.error('Guest display name must be between 3 and 24 characters');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/guest`, {
        display_name: trimmedGuestName
      });
      
      localStorage.setItem('token', response.data.access_token);
      setUser(response.data.user);
      
      toast.success('Logged in as Guest! (Limited access)');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail) && detail.length > 0 && detail[0]?.msg) {
        toast.error(detail[0].msg);
      } else if (typeof detail === 'string' && detail.trim()) {
        toast.error(detail);
      } else {
        toast.error('Failed to login as guest');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4">
      <div className="w-full max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src="/logo.png" alt="ChatHub India" className="h-16 w-auto" />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
            ChatHub
          </h1>
          <p className="text-gray-600">Real-time conversations, anywhere</p>
        </div>

        <Card data-testid="auth-card" className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader>
            <CardTitle data-testid="auth-title">
              {showOtpInput ? 'Verify Your Email' : (isLogin ? 'Welcome Back' : 'Create Account')}
            </CardTitle>
            <CardDescription data-testid="auth-description">
              {showOtpInput 
                ? `Enter the 6-digit OTP sent to ${registrationEmail}` 
                : (isLogin ? 'Sign in to continue to your chats' : 'Sign up to start chatting')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {showOtpInput ? (
              // OTP Verification Form
              <form onSubmit={handleOtpVerification} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="otp">One-Time Password (OTP)</Label>
                  <Input
                    id="otp"
                    name="otp"
                    type="text"
                    maxLength="6"
                    pattern="[0-9]{6}"
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    required
                    disabled={loading}
                    className="text-center text-2xl tracking-widest"
                  />
                  <p className="text-xs text-gray-500">OTP expires in 10 minutes</p>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  disabled={loading || otp.length !== 6}
                >
                  {loading ? 'Verifying...' : 'Verify & Register'}
                </Button>

                <div className="text-center space-y-2">
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={loading}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Resend OTP
                  </button>
                  <br />
                  <button
                    type="button"
                    onClick={() => {setShowOtpInput(false); setOtp(''); setRegistrationEmail('');}}
                    className="text-sm text-gray-600 hover:text-gray-700"
                  >
                    ← Back to registration
                  </button>
                </div>
              </form>
            ) : (
              // Login/Register Form
              <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  name="username"
                  data-testid="username-input"
                  placeholder="Enter your username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="email-input"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <PasswordInput
                  id="password"
                  name="password"
                  data-testid="password-input"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
                {isLogin && (
                  <div className="text-right">
                    <Link
                      to="/forgot-password"
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                      data-testid="forgot-password-link"
                    >
                      Forgot Password?
                    </Link>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                data-testid="auth-submit-button"
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-200"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    {isLogin ? 'Signing in...' : 'Creating account...'}
                  </span>
                ) : (
                  <span>{isLogin ? 'Sign In' : 'Sign Up'}</span>
                )}
              </Button>
            </form>
            )}

            {!showOtpInput && (
            <div className="mt-6 text-center space-y-3">
              <button
                data-testid="toggle-auth-mode-button"
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors block w-full"
                disabled={loading}
              >
                {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
              </button>
              
              {isLogin && (
                <div className="space-y-2">
                  <Input
                    placeholder="Guest display name"
                    value={guestDisplayName}
                    onChange={(e) => setGuestDisplayName(e.target.value)}
                    disabled={loading}
                    maxLength={24}
                  />
                  <button
                    data-testid="guest-login-button"
                    type="button"
                    onClick={handleGuestLogin}
                    className="text-sm text-gray-600 hover:text-gray-700 font-medium transition-colors block w-full"
                    disabled={loading}
                  >
                    Continue as Guest
                  </button>
                </div>
              )}
            </div>
            )}
          </CardContent>
        </Card>
      </div>
      <Footer user={null} />
    </div>
  );
};

export default AuthPage;
